import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/form';
import { errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Logo } from '@/components/Logo';

export function LoginPage() {
  const { me, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (me) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_1.1fr]">
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm" noValidate>
          <Logo className="mb-10" />
          <h1 className="text-2xl font-semibold">כניסה למערכת</h1>
          <p className="mt-1 text-sm text-muted">ניהול לידים, לקוחות והזמנות של Takumi Matcha</p>
          <div className="mt-8 flex flex-col gap-4">
            <Field label="אימייל">
              {(id) => (
                <Input id={id} type="email" dir="ltr" className="text-start" autoComplete="username" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} />
              )}
            </Field>
            <Field label="סיסמה">
              {(id) => (
                <Input id={id} type="password" dir="ltr" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              )}
            </Field>
            {error && (
              <p role="alert" className="rounded-control bg-overdue-bg px-3 py-2 text-sm font-medium text-overdue">
                {error}
              </p>
            )}
            <Button type="submit" size="lg" loading={busy} className="mt-2">
              כניסה
            </Button>
          </div>
        </form>
      </div>
      <div className="relative hidden overflow-hidden bg-matcha-900 lg:block" aria-hidden>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,var(--color-matcha-700),transparent_60%)]" />
        <svg className="absolute -bottom-24 -start-24 size-[520px] text-matcha-800" viewBox="0 0 200 200" fill="none">
          <circle cx="100" cy="100" r="90" stroke="currentColor" strokeWidth="14" strokeDasharray="420 200" strokeLinecap="round" />
        </svg>
        <div className="relative flex h-full flex-col justify-end p-14 text-matcha-100">
          <p className="font-display text-4xl leading-tight font-medium text-white">
            כל ליד מקבל
            <br />
            את הפולואפ הבא שלו.
          </p>
          <p className="mt-4 max-w-md text-matcha-200">
            מהפנייה באתר, דרך הדוגמית והצעת המחיר — ועד ההזמנה החוזרת.
          </p>
        </div>
      </div>
    </div>
  );
}
